<script setup lang="ts">
defineProps<{
  currentStep: number | 'review'
  totalSteps?: number
}>()

const emit = defineEmits<{
  'step-click': [step: number]
}>()

const steps = [
  { number: 1, label: 'Mission' },
  { number: 2, label: 'Subject' },
  { number: 3, label: 'Objectives' },
  { number: 4, label: 'Comms' },
]

function stepState(n: number, current: number | 'review'): 'active' | 'completed' | 'future' {
  if (current === 'review') return 'completed'
  if (n === current) return 'active'
  if (n < current) return 'completed'
  return 'future'
}

function handleClick(n: number, current: number | 'review') {
  const cur = current === 'review' ? 5 : current
  if (n < cur) emit('step-click', n)
}
</script>

<template>
  <div class="flex items-center justify-center gap-0 px-4 py-4">
    <template v-for="(step, idx) in steps" :key="step.number">
      <!-- Step node -->
      <div class="flex flex-col items-center">
        <button
          type="button"
          class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors focus:outline-none"
          :class="{
            'bg-primary-container text-white': stepState(step.number, currentStep) === 'active',
            'bg-primary-container text-white cursor-pointer hover:opacity-80': stepState(step.number, currentStep) === 'completed',
            'bg-surface-container-highest text-outline cursor-default': stepState(step.number, currentStep) === 'future',
          }"
          :disabled="stepState(step.number, currentStep) === 'future'"
          @click="handleClick(step.number, currentStep)"
        >
          <svg
            v-if="stepState(step.number, currentStep) === 'completed'"
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <span v-else>{{ step.number }}</span>
        </button>
        <span
          class="text-xs mt-1 font-mono"
          :class="{
            'text-primary': stepState(step.number, currentStep) === 'active',
            'text-outline': stepState(step.number, currentStep) !== 'active',
          }"
        >
          {{ step.label }}
        </span>
      </div>

      <!-- Connector line -->
      <div
        v-if="idx < steps.length - 1"
        class="flex-1 h-0.5 mx-1 mb-4"
        :class="
          stepState(step.number + 1, currentStep) !== 'future' || currentStep === 'review'
            ? 'bg-primary-container'
            : 'bg-surface-container-highest'
        "
      />
    </template>

    <!-- Connector to review node -->
    <div
      class="flex-1 h-0.5 mx-1 mb-4"
      :class="currentStep === 'review' ? 'bg-primary-container' : 'bg-surface-container-highest'"
    />

    <!-- Review node -->
    <div class="flex flex-col items-center">
      <div
        class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors"
        :class="
          currentStep === 'review'
            ? 'bg-primary-container text-white'
            : 'bg-surface-container-highest text-outline'
        "
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </div>
      <span
        class="text-xs mt-1 font-mono"
        :class="currentStep === 'review' ? 'text-primary' : 'text-outline'"
      >Review</span>
    </div>
  </div>
</template>
